// src/lib/Head.tsx
import { useEffect } from "react";
import { site } from "./siteMeta";
import { buildOrganizationSchema } from "./structuredData";
import { parsePath, pathOf } from "./routes";

import type { Lang } from "./lang";

/** Mere fleksibel robots-konfiguration (alternativ til noindex) */
export type RobotsOptions = {
  index?: boolean;
  follow?: boolean;
  noarchive?: boolean;
  noimageindex?: boolean;
  notranslate?: boolean;
  maxSnippet?: number; // -1 = ubegrænset
  maxImagePreview?: "large" | "standard" | "none";
  maxVideoPreview?: number; // -1 = ubegrænset
};

export type HeadProps = {
  lang: Lang;
  /** fx "/da" eller "/en/house". Brug samme som du allerede gør. */
  path: string;
  title: string;
  description: string;
  /** Absolut URL eller /public-sti */
  ogImage?: string;
  /** Alternativtekst til delingsbilledet */
  ogImageAlt?: string;
  /** Angiv billedstørrelse for OG (default 1200×630) */
  ogImageWidth?: number;
  ogImageHeight?: number;
  /** (Bagud-komp) Tving noindex/nofollow for denne side */
  noindex?: boolean;
  /** (Ny) Robots som string eller options-objekt */
  robots?: string | RobotsOptions;
  /** (Ny) Meta keywords – lav SEO-vægt, men efterspurgt */
  keywords?: string[];
  /** (Ny) Tving canonical – ellers bygges fra site.baseUrl + path */
  canonical?: string;
  /** JSON-LD objekt eller liste af objekter */
  jsonLd?: object | object[];
};

const ogLocale = (l: Lang) => {
  if (l === "da") return "da_DK";
  if (l === "de") return "de_DE";
  return "en_GB";
};
// FIX: alternate skal være det MODSATTE sprog
const altLocale = (l: Lang) => {
  if (l === "da") return "en_GB";
  if (l === "de") return "en_GB";
  return "da_DK";
};

function absUrl(u: string | undefined) {
  if (!u) return undefined;
  return u.startsWith("http") ? u : `${site.baseUrl.replace(/\/+$/, "")}${u}`;
}

function upsertMeta(
  by: { name?: string; property?: string },
  content?: string
) {
  const sel = by.name
    ? `meta[name="${by.name}"]`
    : `meta[property="${by.property}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(sel);
  if (content == null || content === "") {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    if (by.name) el.setAttribute("name", by.name);
    if (by.property) el.setAttribute("property", by.property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(
  rel: string,
  href?: string,
  attrs: Record<string, string> = {}
) {
  const sel =
    `link[rel="${rel}"]` +
    (attrs["hreflang"] ? `[hreflang="${attrs["hreflang"]}"]` : "");
  let el = document.head.querySelector<HTMLLinkElement>(sel);
  if (href == null || href === "") {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (attrs["hreflang"]) el.setAttribute("hreflang", attrs["hreflang"]);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** Default-robots: noindex i preview (VITE_ROBOTS_NOINDEX=1) – ellers index/follow */
function defaultRobotsString(): string {
  const mode =
    import.meta.env?.MODE ||
    (typeof process !== "undefined" ? process.env.NODE_ENV : "production");

  interface ImportMetaEnv {
    VITE_ROBOTS_NOINDEX?: string;
    MODE?: string;
  }
  interface ImportMeta {
    env: ImportMetaEnv;
  }

  const forceNoindex =
    (import.meta as ImportMeta)?.env?.VITE_ROBOTS_NOINDEX === "1" ||
    (typeof process !== "undefined" && process.env.VITE_ROBOTS_NOINDEX === "1");
  const isProd = mode === "production" && !forceNoindex;

  return robotsToString({
    index: isProd,
    follow: isProd,
    maxSnippet: -1,
    maxImagePreview: "large",
    maxVideoPreview: -1,
  });
}

function googleSiteVerificationToken(): string {
  const token =
    (import.meta as { env?: { VITE_GOOGLE_SITE_VERIFICATION?: string } })?.env
      ?.VITE_GOOGLE_SITE_VERIFICATION ||
    (typeof process !== "undefined"
      ? process.env.VITE_GOOGLE_SITE_VERIFICATION
      : "");

  return String(token || "").trim();
}

function robotsToString(r: RobotsOptions): string {
  const parts: string[] = [];
  parts.push(r.index === false ? "noindex" : "index");
  parts.push(r.follow === false ? "nofollow" : "follow");
  if (r.noarchive) parts.push("noarchive");
  if (r.noimageindex) parts.push("noimageindex");
  if (r.notranslate) parts.push("notranslate");
  parts.push(`max-snippet:${r.maxSnippet ?? -1}`);
  parts.push(`max-image-preview:${r.maxImagePreview ?? "large"}`);
  parts.push(`max-video-preview:${r.maxVideoPreview ?? -1}`);
  return parts.join(", ");
}

const breadcrumbNames: Record<
  Lang,
  Record<
    | "home"
    | "house"
    | "area"
    | "gallery"
    | "faq"
    | "contact"
    | "book"
    | "fees"
    | "privacy",
    string
  >
> = {
  da: {
    home: "Forside",
    house: "Sommerhuset",
    area: "Området",
    gallery: "Galleri",
    faq: "Ofte stillede spørgsmål",
    contact: "Kontakt",
    book: "Booking",
    fees: "Gebyrer",
    privacy: "Privatlivspolitik",
  },
  en: {
    home: "Home",
    house: "The House",
    area: "Area",
    gallery: "Gallery",
    faq: "Frequently Asked Questions",
    contact: "Contact",
    book: "Book",
    fees: "Fees",
    privacy: "Privacy Policy",
  },
  de: {
    home: "Startseite",
    house: "Das Haus",
    area: "Umgebung",
    gallery: "Galerie",
    faq: "Häufig gestellte Fragen",
    contact: "Kontakt",
    book: "Buchung",
    fees: "Gebühren",
    privacy: "Datenschutzrichtlinie",
  },
};

function buildBreadcrumbJsonLd(lang: Lang, path: string, canonicalHref: string, title: string) {
  const parsed = parsePath(path);
  const labels = breadcrumbNames[lang];

  if (!parsed) {
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: labels.home,
          item: `${site.baseUrl}/${lang}`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: title,
          item: canonicalHref,
        },
      ],
    };
  }

  const rootItem = {
    "@type": "ListItem",
    position: 1,
    name: labels.home,
    item: `${site.baseUrl}/${lang}`,
  };

  if (parsed.key === "home") {
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [rootItem],
    };
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      rootItem,
      {
        "@type": "ListItem",
        position: 2,
        name: labels[parsed.key] ?? title,
        item: canonicalHref,
      },
    ],
  };
}

export default function Head({
  lang,
  path,
  title,
  description,
  ogImage,
  ogImageAlt,
  ogImageWidth,
  ogImageHeight,
  noindex,
  robots,
  keywords,
  canonical,
  jsonLd,
}: HeadProps) {
  useEffect(() => {
    // <html lang>
    const html = document.documentElement;
    html.setAttribute("lang", lang);
    html.setAttribute("dir", "ltr");

    // <title>
    document.title = title;

    // Canonical + hreflang (da/en/de/x-default)
    const base = site.baseUrl.replace(/\/+$/, "");
    const parsedPath = parsePath(path);
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const canonicalHref =
      canonical ||
      `${base}${parsedPath ? pathOf(lang, parsedPath.key) : normalizedPath}`;
    const hrefDa = parsedPath ? `${base}${pathOf("da", parsedPath.key)}` : undefined;
    const hrefEn = parsedPath ? `${base}${pathOf("en", parsedPath.key)}` : undefined;
    const hrefDe = parsedPath ? `${base}${pathOf("de", parsedPath.key)}` : undefined;
    const hrefDefault = parsedPath ? hrefEn : undefined;

    upsertLink("canonical", canonicalHref);
    upsertLink("alternate", hrefDa, { hreflang: "da-DK" });
    upsertLink("alternate", hrefDe, { hreflang: "de-DE" });
    upsertLink("alternate", hrefEn, { hreflang: "en-GB" });
    upsertLink("alternate", hrefDefault, { hreflang: "x-default" });

    // Meta: description
    upsertMeta({ name: "description" }, description);

    // Robots (prioritet: robots-prop > noindex-prop > default)
    let robotsStr: string;
    if (typeof robots === "string") robotsStr = robots;
    else if (typeof robots === "object") robotsStr = robotsToString(robots);
    else if (noindex)
      robotsStr =
        "noindex,nofollow,max-snippet:-1,max-image-preview:large,max-video-preview:-1";
    else robotsStr = defaultRobotsString();

    upsertMeta({ name: "robots" }, robotsStr);
    upsertMeta({ name: "googlebot" }, robotsStr);
    upsertMeta({ name: "bingbot" }, robotsStr);
    upsertMeta(
      { name: "google-site-verification" },
      googleSiteVerificationToken() || undefined
    );

    // Keywords (fjern hvis tom/ikke angivet)
    upsertMeta(
      { name: "keywords" },
      keywords && keywords.length ? keywords.join(", ") : undefined
    );

    // Open Graph / Twitter
    const imageAbs = absUrl(ogImage || site.defaultImage)!;
    const locale = ogLocale(lang);
    const localeAlt = altLocale(lang);

    upsertMeta({ property: "og:site_name" }, site.name);
    upsertMeta({ property: "og:type" }, "website");
    upsertMeta({ property: "og:locale" }, locale);
    upsertMeta({ property: "og:title" }, title);
    upsertMeta({ property: "og:description" }, description);
    upsertMeta({ property: "og:url" }, canonicalHref);
    upsertMeta({ property: "og:image" }, imageAbs);
    upsertMeta({ property: "og:image:alt" }, ogImageAlt || title);
    upsertMeta({ property: "og:image:width" }, String(ogImageWidth ?? 1200));
    upsertMeta({ property: "og:image:height" }, String(ogImageHeight ?? 630));
    upsertMeta({ property: "og:locale:alternate" }, localeAlt);

    upsertMeta({ name: "twitter:card" }, "summary_large_image");
    if (site.twitter) upsertMeta({ name: "twitter:site" }, site.twitter);
    upsertMeta({ name: "twitter:title" }, title);
    upsertMeta({ name: "twitter:description" }, description);
    upsertMeta({ name: "twitter:image" }, imageAbs);
    upsertMeta({ name: "twitter:image:alt" }, ogImageAlt || title);

    // JSON-LD
    const defaultJsonLd = [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: site.name,
        url: site.baseUrl,
        description: site.description,
        publisher: {
          "@type": "Organization",
          name: site.publisher.name,
          logo: site.publisher.logo,
        },
      },
      buildOrganizationSchema(),
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: title,
        description,
        url: canonicalHref,
      },
      buildBreadcrumbJsonLd(lang, path, canonicalHref, title),
    ];

    const jsonLdItems = [
      ...defaultJsonLd,
      ...(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []),
    ];

    jsonLdItems.forEach((item, index) => {
      const id = `app-jsonld-${index}`;
      const old = document.getElementById(id);
      if (old) old.remove();
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = id;
      script.text = JSON.stringify(item);
      document.head.appendChild(script);
    });
  }, [
    lang,
    path,
    title,
    description,
    ogImage,
    ogImageAlt,
    ogImageWidth,
    ogImageHeight,
    noindex,
    robots,
    keywords,
    canonical,
    jsonLd,
  ]);

  return null;
}
